import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import RefreshIcon from "@mui/icons-material/Refresh";
import BuildIcon from "@mui/icons-material/Build";
import CategoryIcon from "@mui/icons-material/Category";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { selectUserDb } from "../store/slices/session/sessionSelector";
import {
  useGetWipMetricsQuery,
  WIPCampaignMetrics,
} from "../store/apis/wipApi";
import {
  useBackfillSelectedIosMutation,
  useBackfillBriefingCategoryMutation,
} from "../store/apis/adminApi";
import BuyMediaWipTable from "../components/wip/buyMedia/BuyMediaWipTable";
import {
  DIRECT_BUY_OFF_TABLE,
  PAID_MEDIA_TABLE,
} from "../components/wip/buyMedia/tableVariants";
import WipChartsView from "../components/wip/charts/WipChartsView";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import AssignedUserFilter from "../components/briefings/AssignedUserFilter";
import { useUserRole } from "../hooks/useUserRole";

const monthOptions = (yearsBack = 1, yearsForward = 1) => {
  const now = new Date();
  const opts: { value: string; label: string }[] = [];
  const start = new Date(now.getFullYear() - yearsBack, 0, 1);
  const end = new Date(now.getFullYear() + yearsForward, 11, 1);
  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value, label: `${months[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
};

const currentMonthValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const ACTIVE_TAB_KEY = "beapolo_wip_active_tab";

// Off va entre Buy Media y Gráficas pero con el valor 3, para que la pestaña
// que cada navegador tiene guardada siga apuntando a la misma.
const TAB_BUY_MEDIA_OFF = 3;

const readActiveTab = (): number => {
  try {
    const raw = localStorage.getItem(ACTIVE_TAB_KEY);
    return raw === "1" ? 1 : raw === "2" ? 2 : raw === "3" ? 3 : 0;
  } catch {
    return 0;
  }
};

// La persona por la que se filtra, para que siga elegida al volver. Solo es una
// comodidad de quien mira: si el navegador no la guarda, el WIP sale sin filtrar.
const ASSIGNED_USER_KEY = "beapolo_wip_assigned_user";

const readAssignedUser = (): string => {
  try {
    return localStorage.getItem(ASSIGNED_USER_KEY) ?? "";
  } catch {
    return "";
  }
};

const Wip: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const { canAccessControlAndMargin } = useUserRole();

  const canView =
    userDb?.role === "ADMIN" ||
    userDb?.role === "ACCOUNT" ||
    userDb?.role === "TECHNICIAN" ||
    userDb?.role === "PRICING" ||
    userDb?.role === "STRATEGY";
  const isAdmin = userDb?.role === "ADMIN";

  const [month, setMonth] = useState<string>(currentMonthValue());
  const [tab, setTab] = useState<number>(readActiveTab);
  const [assignedUserId, setAssignedUserId] =
    useState<string>(readAssignedUser);

  const handleAssignedUserChange = (userId: string) => {
    setAssignedUserId(userId);
    try {
      if (userId) localStorage.setItem(ASSIGNED_USER_KEY, userId);
      else localStorage.removeItem(ASSIGNED_USER_KEY);
    } catch {
      // ignore quota errors
    }
  };

  const handleTabChange = (_e: React.SyntheticEvent, value: number) => {
    setTab(value);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, String(value));
    } catch {
      // ignore quota errors
    }
  };
  const [backfillIos, { isLoading: isBackfilling }] =
    useBackfillSelectedIosMutation();
  const [backfillBriefingCategory, { isLoading: isBackfillingCategory }] =
    useBackfillBriefingCategoryMutation();

  const handleBackfill = async () => {
    try {
      if (window.confirm("¿Ejecutar backfill (DRY_RUN=false) para IOs?")) {
        const res = await backfillIos({ dryRun: false }).unwrap();
        const data = res.data;
        if (data) {
          alert(
            `Backfill completado:\nProcesadas: ${data.processed}\nCampañas arregladas: ${data.fixedCampaigns}\nIOs seleccionadas creadas: ${data.createdSelectedIOs}\nInternal IDs corregidos: ${data.fixedInternalCampaigns}`
          );
        } else {
          alert("Backfill completado pero sin datos de respuesta.");
        }
      }
    } catch (err) {
      alert("Error ejecutando backfill");
      console.error(err);
    }
  };

  const handleBackfillBriefingCategory = async () => {
    try {
      // 1) Dry run (no escribe)
      const dry = await backfillBriefingCategory({ dryRun: true }).unwrap();
      const d = dry.data;
      if (!d) {
        alert("Backfill (simulación) sin datos de respuesta.");
        return;
      }
      const apply = window.confirm(
        `Simulación de categoría de briefings:\n` +
          `Revisados: ${d.processed}\nCambiarían: ${d.updated}\nSin fechas (no clasificables): ${d.nullCount}\n\n` +
          `¿Aplicar de verdad? (dryRun=false)`
      );
      if (!apply) return;
      // 2) Aplicar real
      const res = await backfillBriefingCategory({ dryRun: false }).unwrap();
      const r = res.data;
      if (r) {
        alert(
          `Backfill de categoría completado:\n` +
            `Revisados: ${r.processed}\nActualizados: ${r.updated}\nSin fechas (no clasificables): ${r.nullCount}`
        );
      } else {
        alert("Backfill completado pero sin datos de respuesta.");
      }
    } catch (err) {
      alert("Error ejecutando backfill de categoría");
      console.error(err);
    }
  };

  const { data, isLoading, isFetching, error, refetch } = useGetWipMetricsQuery(
    { month, assignedUserId: assignedUserId || undefined },
    { refetchOnMountOrArgChange: true }
  );

  const rows: WIPCampaignMetrics[] = useMemo(
    () => (data?.data as WIPCampaignMetrics[] | null) ?? [],
    [data]
  );

  const paidMedia = useMemo(
    () => rows.filter((r) => r.briefingType === "PAID_MEDIA_MANAGED"),
    [rows]
  );
  // Buy Media se parte en dos: lo conectado por API y lo "off", las compras
  // directas cuyos datos se rellenan a mano.
  const buyMedia = useMemo(
    () =>
      rows.filter(
        (r) => r.briefingType === "BUY_MEDIA_DIRECT_AND_OWN" && !r.isDirectBuy
      ),
    [rows]
  );
  const buyMediaOff = useMemo(
    () =>
      rows.filter(
        (r) => r.briefingType === "BUY_MEDIA_DIRECT_AND_OWN" && r.isDirectBuy
      ),
    [rows]
  );

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        {error && (
          <Alert severity="error">
            {t(
              "wip.loadError",
              "Error cargando datos de WIP. Inténtalo de nuevo."
            )}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <OrbLoader
              size="large"
              label={t("common.loading", { defaultValue: "Cargando…" })}
            />
          </Box>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                px: { xs: 1, md: 2 },
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "center" },
                gap: 1.5,
                py: { xs: 1.5, md: 0 },
              }}
            >
              <Tabs value={tab} onChange={handleTabChange}>
                <Tab
                  value={0}
                  label={`${t("wip.tabPaidMedia", "Paid Media (gestionado)")} · ${paidMedia.length}`}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                />
                <Tab
                  value={1}
                  label={`${t("wip.tabBuyMedia", "Buy Media Direct & Own")} · ${buyMedia.length}`}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                />
                <Tab
                  value={TAB_BUY_MEDIA_OFF}
                  label={`${t("wip.tabBuyMediaOff", "Buy Media Direct & Own · Off")} · ${buyMediaOff.length}`}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                />
                <Tab
                  value={2}
                  label={t("wip.tabCharts", "Gráficas")}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                />
              </Tabs>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: { xs: 1, md: 1.5 } }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="wip-month-label">
                    {t("wip.month", "Mes")}
                  </InputLabel>
                  <Select
                    labelId="wip-month-label"
                    label={t("wip.month", "Mes")}
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {monthOptions().map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <AssignedUserFilter
                  value={assignedUserId}
                  onChange={handleAssignedUserChange}
                  currentUserId={userDb?.id}
                />

                <Tooltip title={t("wip.refresh", "Refrescar")}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => refetch()}
                      disabled={isFetching}
                      sx={{
                        border: `1px solid ${theme.palette.primary.light}`,
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {isAdmin && (
                  <Tooltip title={t("wip.fixIosAdmin", "Fix IOs (Admin)")}>
                    <span>
                      <IconButton
                        color="secondary"
                        onClick={handleBackfill}
                        disabled={isBackfilling}
                        sx={{
                          border: `1px solid ${theme.palette.secondary.light}`,
                        }}
                      >
                        <BuildIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {isAdmin && (
                  <Tooltip
                    title={t(
                      "wip.backfillBriefingCategoryAdmin",
                      "Backfill categoría de briefings (Admin)"
                    )}
                  >
                    <span>
                      <IconButton
                        color="secondary"
                        onClick={handleBackfillBriefingCategory}
                        disabled={isBackfillingCategory}
                        sx={{
                          border: `1px solid ${theme.palette.secondary.light}`,
                        }}
                      >
                        <CategoryIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Paper>

            {/* Same configurable table for both tabs: Paid keeps its own view,
                columns and built-in views, and no margin editing. The key
                remounts it so each tab loads its own stored view. Each table
                exports its own CSV with the columns on screen. */}
            {tab === 2 ? (
              <WipChartsView rows={rows} />
            ) : tab === 0 ? (
              <BuyMediaWipTable
                key="paidMedia"
                title="Paid Media (gestionado)"
                rows={paidMedia}
                canEditDirectBuys={canAccessControlAndMargin}
                isAdmin={isAdmin}
                variant={PAID_MEDIA_TABLE}
              />
            ) : tab === TAB_BUY_MEDIA_OFF ? (
              <BuyMediaWipTable
                key="buyMediaOff"
                title="Buy Media Direct & Own · Off"
                rows={buyMediaOff}
                canEditDirectBuys={canAccessControlAndMargin}
                isAdmin={isAdmin}
                variant={DIRECT_BUY_OFF_TABLE}
              />
            ) : (
              <BuyMediaWipTable
                key="buyMedia"
                title="Buy Media Direct & Own"
                rows={buyMedia}
                canEditMargin={canAccessControlAndMargin}
                canEditDirectBuys={canAccessControlAndMargin}
                isAdmin={isAdmin}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Wip;
